import { useRef, useState } from "react";
import { FileText, Upload, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { uploadPdf } from "../../api/upload.api.js";
import { assetUrl } from "../../api/client.js";
import { cn } from "../../lib/cn.js";

/**
 * Single-PDF picker. Stores the relative path. Renders a card with the
 * filename + size when populated, or a drop-zone when empty.
 */
export function PdfUpload({ value, onChange, subdir, label, hint, required, className }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadPdf(file, subdir);
      onChange(result.url);
      setMeta(result);
      toast.success("PDF uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? "PDF upload failed");
    } finally {
      setUploading(false);
    }
  };

  const filename = meta?.originalName ?? value?.split("/").pop();

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label className="block text-xs font-medium uppercase tracking-wider text-text-secondary">
          {label}
          {required && <span className="ml-1 text-danger">*</span>}
        </label>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {value ? (
        <div className="flex items-center justify-between rounded-lg border border-border bg-bg-elevated-2 px-4 py-3">
          <div className="flex items-center gap-3 truncate">
            <FileText className="h-5 w-5 shrink-0 text-accent" />
            <div className="truncate">
              <p className="truncate text-sm text-text-primary">{filename}</p>
              {meta?.size && (
                <p className="text-xs text-text-muted">{(meta.size / 1024).toFixed(0)} KB</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={assetUrl(value)}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md p-1.5 text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="rounded-md px-3 py-1 text-xs text-text-secondary hover:bg-bg-elevated hover:text-text-primary disabled:opacity-50"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={() => {
                onChange("");
                setMeta(null);
              }}
              className="rounded-md p-1.5 text-text-muted hover:bg-danger/10 hover:text-danger"
              title="Remove"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-subtle bg-bg-elevated-2 px-4 py-6 text-sm text-text-muted hover:border-accent hover:text-accent"
        >
          {uploading ? (
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <Upload className="h-5 w-5" />
          )}
          {uploading ? "Uploading…" : "Click to upload PDF (max 25 MB)"}
        </button>
      )}
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}
